import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { api } from "../api/client";
import { Stethoscope, Star, Clock, Search } from "lucide-react";

const INITIALS = (name) => (name || "").split(" ").slice(-2).map((n) => n[0] || "").join("").toUpperCase();
const PALETTE = ["bg-rose-100 text-rose-700", "bg-emerald-100 text-emerald-700", "bg-blue-100 text-blue-700", "bg-amber-100 text-amber-700", "bg-violet-100 text-violet-700", "bg-pink-100 text-pink-700", "bg-cyan-100 text-cyan-700", "bg-orange-100 text-orange-700"];
const pickColor = (id) => PALETTE[(id || "").charCodeAt(0) % PALETTE.length];

const Consultations = () => {
    const [items, setItems] = useState([]);
    const [specs, setSpecs] = useState([]);
    const [filterSpec, setFilterSpec] = useState("");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (filterSpec) params.set("specialization", filterSpec);
        if (q) params.set("q", q);
        api.get(`/doctors?${params.toString()}`).then((r) => {
            setItems(r.data.items || []);
            setSpecs(r.data.specializations || []);
        }).finally(() => setLoading(false));
    }, [filterSpec, q]);

    return (
        <div className="min-h-screen bg-white dark:bg-[#0B0B12]">
            <Navbar />
            <main className="mx-auto max-w-6xl px-6 py-12 lg:px-12">
                <p className="label-uppercase text-zinc-500">Care, on demand</p>
                <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 sm:text-5xl dark:text-zinc-50" style={{ letterSpacing: "-0.02em" }}>Talk to a doctor</h1>
                <p className="mt-2 max-w-xl text-sm text-zinc-500">Verified specialists, secure chat consultations and instant prescriptions when you need them.</p>

                <div className="mt-8 flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[240px] max-w-md">
                        <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
                        <input type="text" placeholder="Search by name, hospital or specialization…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full rounded-full border border-zinc-200 pl-9 pr-4 py-2 text-sm focus:border-brand-400 focus:outline-none" data-testid="doctors-search" />
                    </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid="doctors-spec-chips">
                    <button onClick={() => setFilterSpec("")} className={`chip ${!filterSpec ? "chip-active" : ""}`} data-testid="spec-chip-all">All</button>
                    {specs.map((s) => (
                        <button key={s} onClick={() => setFilterSpec(s)} className={`chip ${filterSpec === s ? "chip-active" : ""}`} data-testid={`spec-chip-${s}`}>
                            {s.replace("-", " ")}
                        </button>
                    ))}
                </div>

                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {loading ? <p className="text-sm text-zinc-500">Loading…</p> : items.length === 0 ? (
                        <p className="rounded-2xl border border-dashed border-zinc-200 p-8 text-center text-sm text-zinc-500 sm:col-span-2 lg:col-span-3">No doctors match this filter.</p>
                    ) : items.map((d) => (
                        <Link key={d.id} to={`/consultations/doctor/${d.id}`} className="group block rounded-2xl border border-zinc-100 bg-white p-5 transition-shadow hover:shadow-md dark:bg-white/[0.03] dark:border-white/10" data-testid={`doctor-card-${d.id}`}>
                            <div className="flex items-start gap-4">
                                <span className={`flex h-14 w-14 items-center justify-center rounded-full text-lg font-semibold ${pickColor(d.id)}`}>{INITIALS(d.full_name)}</span>
                                <div className="min-w-0 flex-1">
                                    <p className="font-medium text-zinc-900 dark:text-zinc-50">{d.full_name}</p>
                                    <p className="mt-0.5 text-xs text-zinc-500 capitalize">{d.specialization?.replace("-", " ")} • {d.qualifications}</p>
                                    <p className="mt-1 text-[11px] text-zinc-500 line-clamp-1">{d.hospital_affiliation}</p>
                                </div>
                            </div>
                            <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
                                <div>
                                    <p className="flex items-center justify-center gap-0.5 font-medium text-zinc-900 dark:text-zinc-50"><Star className="h-3 w-3 fill-amber-400 text-amber-500" />{d.rating?.toFixed(1)}</p>
                                    <p className="text-zinc-500">{d.reviews_count} reviews</p>
                                </div>
                                <div>
                                    <p className="font-medium text-zinc-900 dark:text-zinc-50">{d.experience_years}+ yrs</p>
                                    <p className="text-zinc-500">experience</p>
                                </div>
                                <div>
                                    <p className="font-medium text-zinc-900 dark:text-zinc-50">₹{d.consultation_fee}</p>
                                    <p className="text-zinc-500">per consult</p>
                                </div>
                            </div>
                            <p className="mt-4 inline-flex items-center gap-1 rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 group-hover:bg-brand-100"><Stethoscope className="h-3 w-3" /> Book consultation</p>
                        </Link>
                    ))}
                </div>
            </main>
            <Footer />
        </div>
    );
};

export default Consultations;
